/* Copyright (c) Trimble Inc. All rights reserved. */

import { CircularProgress, createStyles, makeStyles, Theme, Typography } from '@material-ui/core';
import clsx from 'clsx';
import type { ReactElement } from 'react';
import React from 'react';

export interface LoaderProps {
  showBackDrop?: boolean;
  message?: string | ReactElement;
  relative?: boolean;
  highlight?: boolean;
}

export const useLoaderStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: '100%',
      display: 'flex',
      position: 'relative',
      alignItems: 'center',
      alignContent: 'center',
      justifyContent: 'center',
      flexDirection: 'column',
    },
    relative: {
      position: 'absolute',
      top: 'calc( 50% - 58px)',
    },
    highlight: {
      backgroundColor: `${theme?.palette?.grey['A100']}50`,
    },
    container: {
      padding: '15px',
      margin: 'auto',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      borderRadius: '2px',
    },
    backdrop: {
      width: '100vw',
      height: '100vh',
      display: 'flex',
      position: 'fixed',
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      zIndex: 10,
      top: 0,
      left: 0,
    },
  })
);

/**
 * @component Loader
 * @param showBackDrop
 * @returns Loader View
 */
export const Loader: React.FC<LoaderProps> = React.memo(
  ({ showBackDrop = false, message, relative = false, highlight = false }) => {
    const classes = useLoaderStyles();
    return (
      <div
        data-testid="loader"
        className={clsx(classes.root, {
          [classes.backdrop]: showBackDrop,
          [classes.relative]: relative,
        })}
      >
        <div
          className={clsx([
            classes.container,
            { [classes.highlight]: highlight },
          ])}
        >
          <CircularProgress />
          {message && (
            <Typography
              variant="body1"
              color="primary"
              style={{ marginTop: 1, backgroundColor: highlight ? '#eff0f2' : 'inherit' }}
            >
              {message}
            </Typography>
          )}
        </div>
      </div>
    );
  }
);

export default Loader;
